import React, { useState, useCallback } from "react";

//Common Styles
import { 
  JsonViewerContainer, 
  JsonViewerContent, 
  JsonViewerHeader, 
  JsonViewerTitle, 
  JsonViewerActions, 
  JsonViewerCopyButton, 
  JsonViewerEmptyState, 
  JsonViewerEmptyIcon,
  JsonViewerSyntaxHighlight,
  TableWrapper 
} from "../../styles/commonStyles";

//Types
type JsonViewerProps = {
  data: any;
  title?: string;
  maxHeight?: string;
  enableSyntaxHighlight?: boolean;
  enableCopy?: boolean;
  collapsible?: boolean;
};

const PartyJsonViewer = ({ 
  data, 
  title = "JSON Viewer", 
  maxHeight = "55vh",
  enableSyntaxHighlight = true,
  enableCopy = true,
  collapsible = false 
}: JsonViewerProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    }
  }, [data]);

  const formatJsonValue = useCallback((value: any, key?: string): React.ReactNode => {
    if (!enableSyntaxHighlight) {
      return JSON.stringify(value);
    }

    if (value === null) {
      return <JsonViewerSyntaxHighlight type="null">null</JsonViewerSyntaxHighlight>;
    }
    
    if (typeof value === "boolean") {
      return <JsonViewerSyntaxHighlight type="boolean">{String(value)}</JsonViewerSyntaxHighlight>;
    }
    
    if (typeof value === "number") {
      return <JsonViewerSyntaxHighlight type="number">{String(value)}</JsonViewerSyntaxHighlight>;
    }
    
    if (typeof value === "string") {
      return <JsonViewerSyntaxHighlight type="string">"{value}"</JsonViewerSyntaxHighlight>;
    }
    
    if (key) {
      return <JsonViewerSyntaxHighlight type="key">"{key}"</JsonViewerSyntaxHighlight>;
    }
    
    return String(value);
  }, [enableSyntaxHighlight]);

  const renderHighlightedJson = useCallback((obj: any, indent = 0): React.ReactNode => {
    if (obj === null || typeof obj !== "object") {
      return formatJsonValue(obj);
    }

    const indentStr = "  ".repeat(indent);
    const entries = Object.entries(obj);
    
    if (entries.length === 0) {
      return (
        <>
          <JsonViewerSyntaxHighlight type="bracket">{"{"}</JsonViewerSyntaxHighlight>
          <JsonViewerSyntaxHighlight type="bracket">{"}"}</JsonViewerSyntaxHighlight>
        </>
      );
    }

    return (
      <>
        <JsonViewerSyntaxHighlight type="bracket">{"{"}</JsonViewerSyntaxHighlight>
        {entries.map(([key, value], index) => (
          <div key={key} style={{ marginLeft: indentStr === "" ? 0 : 20 }}>
            {indentStr}
            {formatJsonValue(key)}
            <JsonViewerSyntaxHighlight type="bracket">: </JsonViewerSyntaxHighlight>
            {typeof value === "object" && value !== null 
              ? renderHighlightedJson(value, indent + 1)
              : formatJsonValue(value)
            }
            {index < entries.length - 1 && <JsonViewerSyntaxHighlight type="bracket">,</JsonViewerSyntaxHighlight>}
          </div>
        ))}
        {indentStr}
        <JsonViewerSyntaxHighlight type="bracket">{"}"}</JsonViewerSyntaxHighlight>
      </>
    );
  }, [formatJsonValue]);

  if (!data) {
    return (
      <TableWrapper>
        <JsonViewerEmptyState>
          <JsonViewerEmptyIcon>📄</JsonViewerEmptyIcon>
          <div>No data available</div>
        </JsonViewerEmptyState>
      </TableWrapper>
    );
  }

  const jsonString = JSON.stringify(data, null, 2);
  const displayContent = enableSyntaxHighlight ? renderHighlightedJson(data) : jsonString;

  return (
    <TableWrapper>
      <JsonViewerHeader>
        <JsonViewerTitle>{title}</JsonViewerTitle>
        <JsonViewerActions>
          {collapsible && (
            <JsonViewerCopyButton onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? "▼" : "▶"} {isExpanded ? "Collapse" : "Expand"}
            </JsonViewerCopyButton>
          )}
          {enableCopy && (
            <JsonViewerCopyButton onClick={handleCopy}>
              📋 {copySuccess ? "Copied!" : "Copy"}
            </JsonViewerCopyButton>
          )}
        </JsonViewerActions>
      </JsonViewerHeader>
      
      {isExpanded && (
        <JsonViewerContainer style={{ maxHeight }}>
          <JsonViewerContent>
            {displayContent}
          </JsonViewerContent>
        </JsonViewerContainer>
      )}
    </TableWrapper>
  );
};

export default PartyJsonViewer;
